<template>
  <div>
    <div v-if="!presentLoader" class="full-height">
      <v-row justify="center" style="height: 90%">
        <v-col cols="11">
          <v-card class="pa-6 my-4" width="100%" raised height="100%">
            <div class="d-flex align-center">
              <v-icon color="primary" @click="onClickPrevBtn()"
                >arrow_back</v-icon
              >
              <div class="pl-2 text-h5 primary--text font-weight-medium">
                Joining Kit
              </div>
            </div>

            <!-- Main Content Row: Document Viewer on Left, Upload on Right -->
            <v-row class="mt-4">
              <!-- Left Side: Document Viewer -->
              <v-col cols="12" md="7">
                <v-card
                  v-if="documentData && documentData.title"
                  class="pa-4"
                  outlined
                  style="height: 100%"
                >
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="text-h6 primary--text">
                      {{ documentData.title }}
                    </div>
                    <v-btn color="primary" @click="showFilePreview()">
                      <v-icon left>fas fa-download</v-icon>
                      Download
                    </v-btn>
                  </div>
                  <v-divider class="mb-4"></v-divider>
                  <div
                    id="documentContentViewer"
                    v-html="documentData.resolvedContent"
                    class="ck-content document-container elevation-4"
                    style="max-height: 600px; overflow-y: auto"
                  />
                </v-card>
                <v-card v-else class="pa-4" outlined style="height: 100%">
                  <div class="text-center grey--text">
                    <v-icon size="64" color="grey lighten-1"
                      >description</v-icon
                    >
                    <p class="mt-2">No document available</p>
                  </div>
                </v-card>
              </v-col>

              <!-- Right Side: Document Upload Section -->
              <v-col cols="12" md="5">
                <v-card class="pa-4" outlined style="height: 100%">
                  <div class="text-h6 primary--text mb-2">Upload Document</div>
                  <v-divider class="mb-3"></v-divider>

                  <!-- Instructions -->
                  <v-alert dense outlined type="info" class="mb-4">
                    <div class="text-body-2">
                      <strong>Instructions:</strong>
                      <ul class="mt-2 ml-4">
                        <li>Review the document details below</li>
                        <li>Upload the required document file</li>
                        <li>
                          Supported formats: PDF, DOC, DOCX, JPG, JPEG, PNG
                        </li>
                        <li>Maximum file size: 10 MB</li>
                        <li>Click "Save & Continue" when done</li>
                      </ul>
                    </div>
                  </v-alert>

                  <div style="max-height: 600px; overflow-y: auto">
                    <v-form
                      v-for="(document, index) in addDocuments"
                      :key="index"
                      :ref="'documentForm' + index"
                    >
                      <v-card
                        class="mb-4 pa-3"
                        outlined
                        :color="index % 2 === 0 ? 'grey lighten-5' : 'white'"
                      >
                        <div
                          v-if="
                            (addDocuments.length > 1 || document.Document_Id) &&
                            document.Mandatory == 'No'
                          "
                          class="d-flex justify-end mb-2"
                        >
                          <v-btn
                            color="error"
                            text
                            small
                            @click="removeDocuments(index, document)"
                          >
                            <v-icon small>delete</v-icon>
                            Remove
                          </v-btn>
                        </div>
                        <ValidationObserver :ref="'documentsForm' + index">
                          <v-row
                            v-if="
                              documentData && Object.keys(documentData).length
                            "
                          >
                            <v-col cols="12">
                              <v-text-field
                                :value="documentData.category || 'N/A'"
                                outlined
                                dense
                                label="Document Category"
                                readonly
                                filled
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12">
                              <v-text-field
                                :value="documentData.formNameAlias || 'N/A'"
                                outlined
                                dense
                                label="Document Type"
                                readonly
                                filled
                              ></v-text-field>
                            </v-col>
                            <v-col
                              v-if="documentData.documentSubType"
                              cols="12"
                            >
                              <v-text-field
                                :value="documentData.documentSubType || 'N/A'"
                                outlined
                                dense
                                label="Document Sub Type"
                                readonly
                                filled
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12">
                              <DragAndDropFiles
                                :ref="'dragAndDropFiles' + index"
                                file-category="document"
                                :old-file-name="document.fileNameWithoutFormat"
                                :is-error="document.fileValidationError"
                                :Mandatory="true"
                                :message="true"
                                :max-file-size-mb="10"
                                @file-event-success="
                                  updateFileName($event, index)
                                "
                                @file-event-triggered="isFileLoading = $event"
                              />
                            </v-col>
                          </v-row>
                        </ValidationObserver>
                      </v-card>
                    </v-form>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <v-divider></v-divider>
            <div class="mt-3 d-flex justify-end">
              <v-btn
                color="primary"
                style="border-radius: 10px"
                large
                :loading="saveLoading"
                :disabled="isFileLoading"
                @click="onSaveDocuments()"
                >Save & Continue</v-btn
              >
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <FilePreviewModal
      v-if="openFilePreviewModal"
      :fileName="filePath"
      folderName="Document Generator"
      :heading="documentData.title"
      :file-name-position="2"
      file-retrieve-type="docusign"
      @close-preview-modal="(openFilePreviewModal = false), (filePath = '')"
    />
    <v-overlay absolute :value="presentLoader" color="#fff" z-index="1">
      <v-progress-circular color="primary" indeterminate size="50" />
    </v-overlay>
  </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import DragAndDropFiles from "@/components/custom-components/DragAndDropFiles.vue";
import FilePreviewModal from "@/components/custom-components/FilePreviewModal.vue";
import { scrollScreenToTop } from "../../../helper";
import {
  RETRIEVE_DOCUMENT_DETAILS,
  ADD_DOCUMENT,
  UPDATE_DOC_DOCUMENT,
  DELETE_DOC_DOCUMENT,
  GET_ALL_DOCUMENTS,
  GENERATE_PLACEHOLDER_RESULT,
  ADD_UPDATE_EMPLOYEE_GENERATED_DOCUMENT,
} from "../../../graphql/OnboardingQueries";

export default {
  name: "JoiningKit",
  props: {
    joiningKitDocumentDetails: {
      type: Object,
      default: () => ({}),
    },
    candidateId: {
      type: Number,
      default: 0,
    },
  },

  components: {
    ValidationObserver,
    DragAndDropFiles,
    FilePreviewModal,
  },

  data: () => ({
    documentData: null,
    addDocuments: [
      {
        Document_Name: "",
        Document_Category: 0,
        Document_Type: 0,
        Document_Sub_Type: 0,
        File_Name: "", // Original file name for display
        fileNameWithoutFormat: "", // Full S3 path for saving
        File_Path: "",
        File_Size: "",
        File_Type: "",
        File_Format: "",
        Mandatory: "No",
        fileValidationError: "",
      },
    ],
    saveLoading: false,
    apiCallCount: 0,
    isFileLoading: false,
    generatePlaceHolderResultLoader: false,
    openFilePreviewModal: false,
    generateDocumentLoading: false,
    getDocumentLoading: false,
    filePath: "",
    reportDetails: "",
  }),
  computed: {
    presentLoader() {
      return (
        this.getDocumentLoading ||
        this.generateDocumentLoading ||
        this.generatePlaceHolderResultLoader
      );
    },
  },

  mounted() {
    this.generatePlaceHolderResult();
  },

  methods: {
    getDocumentDetails() {
      let vm = this;
      vm.getDocumentLoading = true;
      vm.$apollo
        .query({
          query: RETRIEVE_DOCUMENT_DETAILS,
          variables: {
            reportDetails: vm.reportDetails,
          },
          client: "apolloClientB",
          fetchPolicy: "no-cache",
        })
        .then((res) => {
          vm.documentData =
            res?.data?.retrieveDocumentDetails?.documentDetails || {};
          vm.getDocumentLoading = false;
          // After document details are loaded, fetch uploaded documents
          vm.getUploadedDocuments();
          vm.addUpdateEmployeeGeneratedDocument();
        })
        .catch((err) => {
          vm.getDocumentLoading = false;
          let snackbarData = {
            isOpen: true,
            message:
              err?.graphQLErrors?.length !== 0
                ? err.graphQLErrors[0].message
                : "Something went wrong. It could be a possibility due to network connectivity error..",
            type: "warning",
          };
          vm.showAlert(snackbarData);
        });
    },
    generatePlaceHolderResult() {
      let vm = this;
      vm.generatePlaceHolderResultLoader = true;
      vm.$apollo
        .query({
          query: GENERATE_PLACEHOLDER_RESULT,
          client: "apolloClientF",
          variables: {
            urlHash: localStorage.getItem("urlHash"),
          },
          fetchPolicy: "no-cache",
        })
        .then((res) => {
          vm.reportDetails =
            res?.data?.generateOnbodingPlaceHolderResult?.reportDetails || "";
          vm.generatePlaceHolderResultLoader = false;
          vm.getDocumentDetails();
        })
        .catch((err) => {
          vm.generatePlaceHolderResultLoader = false;
          vm.getDocumentDetails();
          let snackbarData = {
            isOpen: true,
            message:
              err?.graphQLErrors?.length !== 0
                ? err.graphQLErrors[0].message
                : "Something went wrong. It could be a possibility due to network connectivity error..",
            type: "warning",
          };
          vm.showAlert(snackbarData);
        });
    },
    addUpdateEmployeeGeneratedDocument(calledFrom = "") {
      let vm = this,
        generatedDocumentId = 0,
        documentLink = "";
      if (
        calledFrom !== "update" &&
        vm.documentData &&
        vm.documentData.generatedDocumentId &&
        vm.documentData.s3Link
      )
        return;
      vm.generateDocumentLoading = true;
      vm.$apollo
        .mutate({
          mutation: ADD_UPDATE_EMPLOYEE_GENERATED_DOCUMENT,
          variables: {
            generatedDocumentId:
              calledFrom === "update"
                ? parseInt(vm.documentData.generatedDocumentId, 10)
                : 0,
            urlHash: localStorage.getItem("urlHash"),
            templateId: vm.documentData?.documentTemplateId
              ? parseInt(vm.documentData.documentTemplateId, 10)
              : 0,
            templateName: vm.documentData?.title || "",
            documentName: vm.documentData?.title || "",
            documentAttachment: vm.documentData?.documentAttachment
              ? parseInt(vm.documentData.documentAttachment, 10)
              : 0,
            documentSubTypeId: vm.documentData?.documentSubTypeId
              ? parseInt(vm.documentData.documentSubTypeId, 10)
              : 0,
            documentContent: vm.documentData?.resolvedContent || "",
            documentHeader: vm.documentData?.templateHeader || "",
            documentFooter: vm.documentData?.templateFooter || "",
            customComponents: [],
            registeredBusinessAddress: vm.documentData
              ?.registeredBusinessAddress
              ? "Main Branch"
              : "Employee Location",
            status: "To Be Discarded",
          },
          client: "apolloClientH",
        })
        .then((res) => {
          ({ generatedDocumentId, documentLink } =
            res?.data?.addUpdateCandidateGeneratedDocument || {});

          if (vm.documentData) {
            vm.documentData.s3Link = documentLink;
            vm.documentData.generatedDocumentId = generatedDocumentId;
          }
          vm.generateDocumentLoading = false;
        })
        .catch((err) => {
          vm.generateDocumentLoading = false;
          let snackbarData = {
            isOpen: true,
            message:
              err?.graphQLErrors?.length !== 0
                ? err.graphQLErrors[0].message
                : "Something went wrong. It could be a possibility due to network connectivity error..",
            type: "warning",
          };
          vm.showAlert(snackbarData);
        });
    },

    getUploadedDocuments() {
      let vm = this;
      vm.$apollo
        .query({
          query: GET_ALL_DOCUMENTS,
          client: "apolloClientA",
          fetchPolicy: "no-cache",
          variables: {
            Org_Code: localStorage.getItem("orgCode"),
            Url_Hash: localStorage.getItem("urlHash"),
          },
        })
        .then((res) => {
          let documents = res?.data?.getAllDocuments?.documents ?? [];

          // Filter for joining kit document based on document name
          // Note: Backend returns incorrect Document_Type_Id, so we filter by Document_Name instead
          if (this.documentData && this.documentData.title) {
            // Find the most recent document with matching name (last one in array)
            let joiningKitDocs = documents.filter(
              (doc) => doc.Document_Name === this.documentData.title
            );
            let joiningKitDoc =
              joiningKitDocs.length > 0
                ? joiningKitDocs[joiningKitDocs.length - 1]
                : null;

            if (joiningKitDoc) {
              // Extract original file name from S3 path format (e.g., "2401?1763699830537?1?docu.pdf" -> "docu.pdf")
              let originalFileName = joiningKitDoc.File_Name
                ? joiningKitDoc.File_Name.split("?")[3] ||
                  joiningKitDoc.File_Name
                : "";

              // Populate the addDocuments array with the uploaded document
              this.addDocuments = [
                {
                  Document_Id: joiningKitDoc.Document_Id,
                  Document_Name: joiningKitDoc.Document_Name,
                  Document_Category: joiningKitDoc.Category_Id,
                  Document_Type: joiningKitDoc.Document_Type_Id,
                  Document_Sub_Type: joiningKitDoc.Document_Sub_Type_Id || 0,
                  File_Name: originalFileName, // Original file name for display
                  fileNameWithoutFormat: joiningKitDoc.File_Name, // Full S3 path for saving
                  File_Path: "",
                  File_Size: "",
                  File_Type: "",
                  File_Format: "",
                  Mandatory: joiningKitDoc.Mandatory || "No",
                  fileValidationError: "",
                },
              ];
            }
          }
        })
        .catch((err) => {
          let snackbarData = {
            isOpen: true,
            message:
              err?.graphQLErrors?.length !== 0
                ? err.graphQLErrors[0].message
                : "Something went wrong. It could be a possibility due to network connectivity error..",
            type: "warning",
          };
          vm.showAlert(snackbarData);
        });
    },

    onClickPrevBtn() {
      this.$emit("back-to-prev");
    },

    addMoreDocuments() {
      this.validateDocuments().then((isValid) => {
        if (isValid) {
          this.addDocuments.push({
            Document_Name: "",
            Document_Category: null,
            Document_Type: null,
            Document_Sub_Type: null,
            File_Name: "",
            File_Path: "",
            File_Size: "",
            File_Type: "",
            File_Format: "",
            Mandatory: "No",
          });
        }
      });
    },

    validateDocuments() {
      return new Promise((resolve) => {
        let callCount = 0,
          isValidCount = 0;
        if (this.addDocuments.length) {
          for (let aIndex in this.addDocuments) {
            // Clear previous validation errors
            this.addDocuments[aIndex].fileValidationError = "";

            this.$refs["documentsForm" + aIndex][0]
              .validate()
              .then((validationResponse) => {
                callCount += 1;
                let aIndexFileName = this.addDocuments[aIndex]["File_Name"];

                // Check if file is uploaded
                if (!aIndexFileName) {
                  this.addDocuments[aIndex].fileValidationError =
                    "Document is required";
                }

                if (validationResponse && aIndexFileName) {
                  isValidCount += 1;
                  if (callCount === this.addDocuments.length) {
                    if (isValidCount === this.addDocuments.length) {
                      resolve(true);
                    } else {
                      resolve(false);
                    }
                  }
                } else {
                  if (callCount === this.addDocuments.length) {
                    resolve(false);
                  }
                }
              });
          }
        } else {
          resolve(true);
        }
      });
    },

    updateFileName(fileName, index) {
      // Extract original file name from S3 path format (e.g., "2401?1763699830537?1?docu.pdf" -> "docu.pdf")
      let originalFileName = fileName ? fileName.split("?")[3] || fileName : "";

      // Store both the original file name (for display) and full S3 path (for saving)
      this.addDocuments[index].File_Name = originalFileName;
      this.addDocuments[index].fileNameWithoutFormat = fileName;

      // Clear validation error when file is uploaded
      if (fileName) {
        this.addDocuments[index].fileValidationError = "";
      } else {
        // Set validation error when file is removed
        this.addDocuments[index].fileValidationError = "Document is required";
      }
    },

    onSaveDocuments() {
      this.validateDocuments().then((isValid) => {
        if (isValid) {
          this.saveLoading = true;
          this.apiCallCount = 0;
          for (let index in this.addDocuments) {
            if (this.addDocuments[index].Document_Id) {
              this.updateDocuments(index, this.addDocuments[index]);
            } else {
              this.addDocument(index, this.addDocuments[index]);
            }
          }
        } else {
          scrollScreenToTop();
        }
      });
    },
    showFilePreview() {
      if (!this.documentData?.s3Link) {
        this.showAlert({
          isOpen: true,
          message: "Something went wrong. Please try again.",
          type: "warning",
        });
        return;
      }
      this.filePath = this.documentData.s3Link;
      this.openFilePreviewModal = true;
    },

    addDocument(index, document) {
      let vm = this;
      vm.$apollo
        .mutate({
          mutation: ADD_DOCUMENT,
          variables: {
            File_Name: document.fileNameWithoutFormat,
            Org_Code: localStorage.getItem("orgCode"),
            Url_Hash: localStorage.getItem("urlHash"),
            Document_Name: this.documentData.title,
            Category_Id: 1,
            Document_Type_Id: this.documentData.formId,
            Sub_Document_Type_Id: this.documentData.documentSubTypeId,
          },
          client: "apolloClientA",
        })
        .then((res) => {
          const savedDoc = res?.data?.addDocument?.documents?.[0];

          // Update the addDocuments array with the saved document data
          if (savedDoc && savedDoc.Document_Id) {
            vm.addDocuments[index].Document_Id = savedDoc.Document_Id;
            vm.addDocuments[index].Document_Name = savedDoc.Document_Name;
            vm.addDocuments[index].Document_Category = savedDoc.Category_Id;
            vm.addDocuments[index].Document_Type = savedDoc.Document_Type_Id;
            vm.addDocuments[index].Document_Sub_Type =
              savedDoc.Document_Sub_Type_Id || 0;
          }

          vm.apiCallCount += 1;
          if (vm.apiCallCount === vm.addDocuments.length) {
            vm.saveLoading = false;
            vm.$emit("joining-kit-saved");
          }
        })
        .catch((err) => {
          vm.saveLoading = false;
          let snackbarData = {
            isOpen: true,
            message:
              err?.graphQLErrors?.length !== 0
                ? err.graphQLErrors[0].message
                : "Something went wrong. It could be a possibility due to network connectivity error..",
            type: "warning",
          };
          vm.showAlert(snackbarData);
          if (vm.apiCallCount === vm.addDocuments.length) {
            vm.saveLoading = false;
          }
        });
    },

    updateDocuments(index, document) {
      let vm = this;
      vm.$apollo
        .mutate({
          mutation: UPDATE_DOC_DOCUMENT,
          variables: {
            Document_Id: document.Document_Id,
            File_Name: document.fileNameWithoutFormat,
            Org_Code: localStorage.getItem("orgCode"),
            Url_Hash: localStorage.getItem("urlHash"),
            Document_Name: document.Document_Name,
            Category_Id: document.Document_Category,
            Document_Type_Id: document.Document_Type,
            Sub_Document_Type_Id: document.Document_Sub_Type,
          },
          client: "apolloClientA",
        })
        .then(() => {
          vm.apiCallCount += 1;
          if (vm.apiCallCount === vm.addDocuments.length) {
            vm.saveLoading = false;
            vm.$emit("joining-kit-saved");
          }
        })
        .catch((err) => {
          let snackbarData = {
            isOpen: true,
            message:
              err?.graphQLErrors?.length !== 0
                ? err.graphQLErrors[0].message
                : "Something went wrong. It could be a possibility due to network connectivity error..",
            type: "warning",
          };
          vm.showAlert(snackbarData);
          if (vm.apiCallCount === vm.addDocuments.length) {
            vm.saveLoading = false;
          }
        });
    },

    removeDocuments(index, document) {
      if (document.Document_Id) {
        let vm = this;
        vm.$apollo
          .mutate({
            mutation: DELETE_DOC_DOCUMENT,
            variables: {
              Org_Code: localStorage.getItem("orgCode"),
              Url_Hash: localStorage.getItem("urlHash"),
              Document_Id: document.Document_Id,
            },
            client: "apolloClientA",
          })
          .then(() => {
            let snackbarData = {
              isOpen: true,
              message: "Document deleted successfully!",
              type: "success",
            };
            vm.showAlert(snackbarData);
            vm.addDocuments.splice(index, 1);
            if (index === 0 && vm.addDocuments.length === 0) {
              vm.addDocuments.push({
                Document_Name: "",
                Document_Category: 0,
                Document_Type: 0,
                Document_Sub_Type: 0,
                File_Name: "",
                File_Path: "",
                File_Size: "",
                File_Type: "",
                File_Format: "",
                Mandatory: "No",
              });
            }
          })
          .catch((err) => {
            let snackbarData = {
              isOpen: true,
              message:
                err?.graphQLErrors?.length !== 0
                  ? err.graphQLErrors[0].message
                  : "Something went wrong. It could be a possibility due to network connectivity error..",
              type: "warning",
            };
            vm.showAlert(snackbarData);
          });
      } else {
        this.addDocuments.splice(index, 1);
      }
    },

    showAlert(snackbarData) {
      this.$store.commit("OPEN_SNACKBAR", snackbarData);
    },
  },
};
</script>
<style scoped>
:deep(.ck-content ol) {
  padding-left: 2em !important;
}

:deep(.ck-content ul) {
  padding-left: 2em !important;
}
.document-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 210mm; /* A4 width */
  margin: 0 auto;
  padding: 10mm; /* A4 margins */
  overflow-y: scroll;
  box-sizing: border-box;
  background: white;
}
</style>
